import { apiBaseUrl, API_CONFIGURED } from "@/lib/config";

/**
 * `getImageVariantUrl` — the original's module 80723, verbatim.
 *
 * Firebase Storage uploads are post-processed into a sibling `<bucket>-variants`
 * bucket holding fixed-size WebP renditions, so a 55px logo does not download a
 * 2 MB original. This rewrites a canonical Storage URL to the variant of the
 * requested size and returns the input untouched for anything it does not
 * recognise (a different host, a non-Storage path, a relative asset).
 *
 * The variant may legitimately not exist yet, which is why every caller pairs
 * this with `useImageVariant`'s 404 fallback.
 */

const STORAGE_HOST = "firebasestorage.googleapis.com";

/** `o` — does the last path segment carry an extension? */
const hasExtension = (path: string): boolean =>
  path.slice(path.lastIndexOf("/") + 1).lastIndexOf(".") > 0;

/** `i` — `photo.jpg` + `64x64` → `photo_64x64.webp`; extension-less keeps the raw suffix. */
const variantPath = (path: string, size: string): string => {
  if (!hasExtension(path)) return `${path}_${size}`;
  return `${path.replace(/\.[^.]+$/, "")}_${size}.webp`;
};

const firebaseExtensionVariantUrl = (
  url: string | undefined,
  size: string,
): string | undefined => {
  if (!url) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.hostname !== STORAGE_HOST) return url;

  // /v0/b/<bucket>/o/<encoded object path>
  const segments = parsed.pathname.split("/");
  if (segments[1] !== "v0" || segments[2] !== "b" || segments[4] !== "o") {
    return url;
  }

  // NOTE: the sibling `-variants` bucket is created by the Firebase "Resize
  // Images" extension, which was installed on the RETIRED prod-managesome
  // project but has NOT been installed on this project's own
  // `online-pos-7a275.firebasestorage.app` bucket (verified: every
  // `online-pos-7a275*-variants` path 404s). So for current URLs this test
  // fails and the full-size original is returned untouched — correct, and the
  // reason `useImageVariant`'s 404 fallback is not being exercised. Installing
  // the Resize Images extension on the new bucket is what would restore the
  // WebP renditions; this function then needs the new suffix handled here too.
  const bucket = segments[3];
  if (!bucket || !bucket.endsWith(".appspot.com")) return url;

  const encoded = segments.slice(5).join("/");
  if (!encoded) return url;

  let objectPath: string;
  try {
    objectPath = decodeURIComponent(encoded);
  } catch {
    return url;
  }

  const variantBucket = `${bucket.replace(/\.appspot\.com$/, "")}-variants`;
  const target = new URL(`https://${STORAGE_HOST}/v0/b/${variantBucket}/o/`);
  target.pathname += encodeURIComponent(variantPath(objectPath, size));
  target.searchParams.set("alt", "media");

  // The variant bucket is public, so the original's download token is dropped —
  // but a cache-busting timestamp, when present, is carried across.
  const timestamp = parsed.searchParams.get("timestamp");
  if (timestamp !== null) target.searchParams.set("timestamp", timestamp);

  return target.toString();
};

/* ==========================================================================
 * ADDITION — resized variants served by THIS project's ordering API.
 *
 * The recovered function above is the original's, verbatim, and on this
 * deployment it is a no-op: it only rewrites `*.appspot.com` buckets fronted
 * by the Firebase "Resize Images" extension, and this project's bucket is
 * `online-pos-7a275.firebasestorage.app` with no such extension installed. So
 * every catalog photograph is delivered at its FULL source size.
 *
 * That is not a bandwidth problem — the JPEGs are 220-580 KB — it is a DECODE
 * problem. A browser expands an image into an uncompressed RGBA surface before
 * it can paint it (width x height x 4 bytes), and measured on butt-karahi-main
 * these are 5168x3448 (68 MB each) up to 8640x5760 (190 MB each), drawn into a
 * menu grid five tiles to a row. This tenant's 57 catalog images total 3.5 GB
 * of decoded memory for one screen, on a phone.
 *
 * So when the original scheme does not apply, fall through to `/img/v1/...` on
 * the ordering API, which resizes on demand, caches to disk and serves the
 * result immutable for a year. See mongo-api/src/routes/imageVariants.js.
 *
 * SSR-SAFE BY CONSTRUCTION. Everything here is a build-time constant — no
 * `window`, no `localStorage`, no capability probe — so the server and the
 * browser emit byte-identical `src` attributes and nothing can hydrate
 * mismatched. The URLs are relative (the same-origin `/api/backend` proxy the
 * rest of the app already uses), which is equally valid in server-rendered
 * HTML and in the browser.
 *
 * IT CANNOT BREAK AN IMAGE:
 *   1. anything unrecognised — a data: URL, an SVG (several catalog items use
 *      one), another host, an empty value — is returned untouched;
 *   2. if the API cannot produce the variant it 302s to the genuine Firebase
 *      download URL and the browser follows it to the real photograph;
 *   3. `useImageVariant`'s existing HEAD-404 fallback, and
 *      `onImageVariantError` for the call sites that do not use the hook, put
 *      the original back.
 * ========================================================================== */

/** The sizes the API will serve. A closed enum, not an arbitrary "?w=" — an
 *  unbounded resize endpoint is a denial-of-service surface. */
export const VARIANT = {
  /** 400 px — menu grid tiles, category chips, cart thumbnails. */
  TILE: "tile",
  /** 800 px — item option drawers and hero imagery. */
  DETAIL: "detail",
  /** 1600 px — full-bleed covers. */
  LARGE: "large",
} as const;

export type VariantSize = (typeof VARIANT)[keyof typeof VARIANT];

const VARIANT_WIDTHS: Array<[VariantSize, number]> = [
  [VARIANT.TILE, 400],
  [VARIANT.DETAIL, 800],
  [VARIANT.LARGE, 1600],
];

const OBJECT_PREFIXES = ["images/", "platforms/", "rider-app/"];

/**
 * Where the variant service lives.
 *
 * `NEXT_PUBLIC_IMAGE_API_ORIGIN` first, so variants can be fronted by a CDN
 * later without touching a component. Otherwise the app's own API base — but
 * ONLY when that base is a relative path (the `/api/backend` proxy, which is
 * the default). An ABSOLUTE base resolves on the server to `localhost:4001`,
 * which would be baked into the HTML and mean nothing to a remote visitor, so
 * in that configuration variants stay off and every consumer keeps rendering
 * originals.
 */
const IMAGE_ORIGIN: string = (() => {
  const explicit = process.env.NEXT_PUBLIC_IMAGE_API_ORIGIN;
  if (explicit) return explicit.replace(/\/+$/, "");
  if (!API_CONFIGURED) return "";
  const base = apiBaseUrl();
  return base.startsWith("/") ? base.replace(/\/+$/, "") : "";
})();

/**
 * Callers inherited from the original ask for a pixel box ("64x64",
 * "512x512"). Map that to the SMALLEST variant that is not smaller than what
 * was asked for, so nothing is ever upscaled or visibly softened.
 */
function variantForRequestedSize(size: VariantSize | string): VariantSize {
  if (VARIANT_WIDTHS.some(([name]) => name === size)) return size as VariantSize;
  const largest = Math.max(
    0,
    ...String(size)
      .split(/[x*]/)
      .map((n) => Number.parseInt(n, 10))
      .filter((n) => Number.isFinite(n)),
  );
  const match = VARIANT_WIDTHS.find(([, width]) => width >= largest);
  return match ? match[0] : VARIANT.LARGE;
}

/** The Storage object path inside one of OUR download URLs, else null. */
function ownObjectPath(url: string): string | null {
  // Strict about the host: the API resolves the path against OUR bucket, so a
  // path lifted from some other host would serve the wrong picture.
  if (!url.includes(`${STORAGE_HOST}/`)) return null;
  const at = url.indexOf("/o/");
  if (at === -1) return null;
  let objectPath: string;
  try {
    objectPath = decodeURIComponent(url.slice(at + 3).split("?")[0]);
  } catch {
    return null;
  }
  if (!objectPath || objectPath.includes("..") || objectPath.includes("\\")) return null;
  if (!OBJECT_PREFIXES.some((p) => objectPath.startsWith(p))) return null;
  // SVG is resolution-independent and already tiny; resizing it would buy a
  // round-trip and a redirect and nothing else.
  if (/\.svgz?$/i.test(objectPath)) return null;
  return objectPath;
}

/**
 * FNV-1a over the whole original URL, its `?token=` included.
 *
 * Rides in the variant URL purely as a cache-buster: variants are served
 * `immutable` for a year, so if an image is ever replaced in place the URL has
 * to change or browsers would keep the old one. Re-uploading mints a new
 * download token, which changes this hash, which changes the URL. The server
 * ignores the value — it keys its own cache on the Storage generation — so it
 * is not a validation surface.
 */
function fingerprint(value: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** The `/img/v1/...` URL for one of our Storage objects, or null. */
function apiVariantUrl(url: string, size: VariantSize | string): string | null {
  if (!IMAGE_ORIGIN) return null;
  const objectPath = ownObjectPath(url);
  if (!objectPath) return null;
  const encoded = objectPath.split("/").map(encodeURIComponent).join("/");
  return `${IMAGE_ORIGIN}/img/v1/${variantForRequestedSize(size)}.webp/${fingerprint(url)}/${encoded}`;
}

/**
 * The variant URL for `url`, or `url` itself when no variant applies.
 *
 * Keeps the original's signature and its every existing caller (including
 * `useImageVariant`), and runs the original's Firebase-extension rewrite FIRST
 * so a deployment that does have that extension behaves exactly as before.
 * Only when that rewrite declines does this reach for our own service.
 */
export const getImageVariantUrl = (
  url: string | undefined,
  size: string,
): string | undefined => {
  const extensionUrl = firebaseExtensionVariantUrl(url, size);
  if (!url || extensionUrl !== url) return extensionUrl;
  return apiVariantUrl(url, size) ?? url;
};

/**
 * Direct (non-hook) form, for components that render a plain <img> and already
 * own their error handling.
 */
export function imageVariantUrl(
  originalUrl: string | null | undefined,
  size: VariantSize = VARIANT.TILE,
): string {
  const original = originalUrl ?? "";
  if (!original) return original;
  return apiVariantUrl(original, size) ?? original;
}

/**
 * `onError` handler that swaps the ORIGINAL back in, once. Guarded with a
 * dataset flag so a genuinely dead original cannot loop.
 */
export function onImageVariantError(originalUrl: string | null | undefined) {
  return (event: { currentTarget: HTMLImageElement }) => {
    const el = event.currentTarget;
    if (!el || !originalUrl || el.dataset.variantFallback === "1") return;
    el.dataset.variantFallback = "1";
    el.src = originalUrl;
  };
}
